import React, { useCallback } from 'react';

import debounce from 'lodash/debounce';

import { padding } from '@lumapps/classnames';
import { DEBOUNCE_DELAY } from '@lumapps/constants';
import { SubSection } from '@lumapps/lumx-layouts/components/SubSection';
import { FlexBox, RadioGroup, RadioButton, Checkbox, TextField } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { WIDGET_IFRAME_KEYS } from '../../keys';
import { IframeSettingsProps, LegacyIframeProperties } from '../../types';

export const IframeSettings: React.FC<IframeSettingsProps> = ({ properties = {}, onChange }) => {
    const { translateKey } = useTranslate();

    const [openInNewTab, setOpenInNewTab] = React.useState<LegacyIframeProperties['openInNewTab']>(
        properties.openInNewTab,
    );

    const debouncedOnChange = debounce(onChange, DEBOUNCE_DELAY);

    const handleChange = useCallback(
        (newProps: LegacyIframeProperties['openInNewTab']) => {
            setOpenInNewTab(newProps);
            debouncedOnChange({ openInNewTab: newProps });
        },
        [debouncedOnChange],
    );

    return (
        <FlexBox orientation="vertical" gap="huge">
            <Checkbox
                isChecked={!!openInNewTab}
                onChange={(checked: boolean) => handleChange(checked ? { customURL: false } : undefined)}
                label={translateKey(WIDGET_IFRAME_KEYS.OPEN_IN_NEW_TAB_LABEL)}
            />
            {openInNewTab && (
                <SubSection className={padding('vertical', 'regular')}>
                    <RadioGroup>
                        <RadioButton
                            onChange={() => handleChange({ ...openInNewTab, customURL: false, url: undefined })}
                            isChecked={!openInNewTab.customURL}
                            label={translateKey(WIDGET_IFRAME_KEYS.USE_IFRAME_URL_LABEL)}
                        />
                        <RadioButton
                            onChange={() => handleChange({ ...openInNewTab, customURL: true })}
                            isChecked={openInNewTab.customURL}
                            label={translateKey(WIDGET_IFRAME_KEYS.USE_CUSTOM_URL_LABEL)}
                        />
                    </RadioGroup>
                    {openInNewTab && openInNewTab.customURL && (
                        <TextField
                            aria-label={translateKey(GLOBAL.URL_FIELD_LABEL)}
                            value={openInNewTab.url ?? ''}
                            onChange={(value: string) => handleChange({ ...openInNewTab, url: value })}
                        />
                    )}
                </SubSection>
            )}
        </FlexBox>
    );
};
